<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { storeToRefs } from 'pinia'
import ProgressSpinner from 'primevue/progressspinner'
import GbButton from '@/components/GbButton.vue'
import LibraryFolderContentSection from './LibraryFolderContentSection.vue'
import ShareLinkInvalidMessage from '@/components/ShareLinkInvalidMessage.vue'
import CopyFolderModal from './CopyFolderModal.vue'
import PremiumActionModal from '@/components/PremiumActionModal.vue'
import { useLibraryStore } from './library.store'
import { useUserStore } from './../authentication/user.store'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { ACTION_TYPES, actionPermissions, CONTENT_TYPES } from "@/util/access_visibility.js";
import LibrarySidebarMenu from './LibrarySidebarMenu.vue'
import { useLibraryFolderAnalytics } from './composables/useLibraryFolderAnalytics'
import { format } from 'date-fns'

const analytics = getAnalyticsTracker()
const libraryStore = useLibraryStore()
const userStore = useUserStore()
const { authInfo, isTrial } = storeToRefs(userStore)
const props = defineProps({
  shareToken: {
    type: String,
    required: true
  }
})

const {
  loadingResources,
  loadFolderError,
  currentFolder,
  goals,
  strategies,
  combinedPresentLevels,
  impactsOfDisabilities,
  resources
} = storeToRefs(libraryStore)
const { getLegacyEventData, getEventData } = useLibraryFolderAnalytics()

const isShareTokenInvalid = ref(false)
const folderOwnerName = ref('')
const openPremiumAction = ref(false)
const openCopyModal = ref(false)

const formattedLastUpdated = computed(() => {
  if (!currentFolder.value?.updated_at) return ''
  return format(new Date(currentFolder.value.updated_at), 'M/d/yy h:mm a')
})

const copyEnabled = computed(() => {
  return currentFolder.value && currentFolder.value.name && !copyPermissionsBlocked.value.block
})

const copyPermissionsBlocked = computed(() => {
  return actionPermissions(ACTION_TYPES.COPY_STUDENT, CONTENT_TYPES.STUDENT, {}, authInfo.value)
})

const copyFolderHandler = () => {
  analytics.sendEvent('Clicked Copy Student Button', { access: copyEnabled.value })
  analytics.sendEvent('Clicked Copy Folder Button', { access: copyEnabled.value })
  if (!copyEnabled.value) {
    openPremiumAction.value = true
    return
  }
  openCopyModal.value = true
}

async function fetchResources() {
  try {
    await libraryStore.fetchResourcesForSharedFolder({
      shareToken: props.shareToken,
      params: {}
    })
    if (currentFolder.value) {
      if (currentFolder.value.owner_name && typeof currentFolder.value.owner_name === 'string') {
        folderOwnerName.value = currentFolder.value.owner_name
      }
      analytics.sendEvent('Viewed Shared Student Library', getLegacyEventData.value) // to be deprecated
      analytics.sendEvent('Viewed Shared Folder', getEventData.value)
    }
  } catch (err) {
    console.error('Error fetching shared folder:', err)
    isShareTokenInvalid.value = true
  }
}

onMounted(async () => {
  await fetchResources()
})
</script>

<template>
  <div>
    <div class="col-span-12 bg-green-100" v-if="!loadingResources && !loadFolderError">
      <div class="container">
        <div class="flex justify-between py-4 items-center">
          <div class="flex items-center space-x-2 text-text-default">
            <span class="material-icons-round text-display-sm">visibility</span>
            <span v-if="folderOwnerName" class="text-lg" data-cy="folder-owner">
              You are viewing a Library folder shared by <span class="font-bold">{{ folderOwnerName }}</span>
            </span>
            <GbButton
              class="whitespace-nowrap !ml-6"
              label="Copy to Your Library"
              gb-theme="greenPrimary"
              @click="copyFolderHandler"
              data-cy="copy-button"
            />
          </div>
          <div class="pl-2">
            <span v-if="formattedLastUpdated" class="text-sm text-gray-900">
              <span class="font-bold">Last updated: </span>{{ formattedLastUpdated }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <div class="grid grid-cols-12 gap-4 container">
      <div class="col-span-12 py-4 pl-4 pr-8 md:col-span-4 lg:col-span-3">
        <LibrarySidebarMenu />
      </div>
      <div class="col-span-12 p-4 md:col-span-8 lg:col-span-9">
        <div v-if="loadingResources && !loadFolderError" class="pl-4 inline-flex items-center">
          <ProgressSpinner strokeWidth="8" class="h-5 w-5 stroke-primary-400" />
          <span class="ml-2">Loading resources...</span>
        </div>
        <div v-else-if="!loadingResources && (loadFolderError || isShareTokenInvalid)" class="flex items-center justify-center pt-12" data-cy="invalid-share-message">
          <ShareLinkInvalidMessage>
            This link is either invalid, or the folder is no longer shared by its owner. Ask the owner of the folder for a new shareable link.
          </ShareLinkInvalidMessage>
        </div>
        <template v-else-if="currentFolder && currentFolder.uuid && !isShareTokenInvalid">
          <div class="grid grid-cols-12 gap-4" data-cy="folder-content">
            <div class="col-span-12 px-4">
              <h1 class="inline-flex items-center overflow-x-hidden">
                <span class="material-icons-round mr-2 text-display-xl text-gray-700">folder</span>
                <span class="overflow-x-hidden text-ellipsis whitespace-nowrap header-md font-medium" data-cy="folder-name">
                  {{ currentFolder.name }}
                </span>
              </h1>

              <LibraryFolderContentSection
                :contentObjects="impactsOfDisabilities"
                contentTypeTitle="Impact of Disability"
                :removableContents="false"
                data-cy="impact-of-disability-section"
                :clickable="!isTrial"
                :shared="true"
              >
                <template #noContentMessage>
                  <span class="text-gray-700">No Impact of Disability statements in this folder</span>
                </template>
              </LibraryFolderContentSection>
              <LibraryFolderContentSection
                :contentObjects="combinedPresentLevels"
                contentTypeTitle="Present Levels"
                :removableContents="false"
                data-cy="present-levels-section"
                :clickable="!isTrial"
                :shared="true"
              >
                <template #noContentMessage>
                  <span class="text-gray-700">No present levels in this folder</span>
                </template>
              </LibraryFolderContentSection>

              <LibraryFolderContentSection
                :contentObjects="goals"
                contentTypeTitle="Goals"
                :saved="true"
                :removableContents="false"
                :shared="true"
                data-cy="goals-section"
                :clickable="!isTrial"
              >
                <template #noContentMessage>
                  <span class="text-gray-700">No goals in this folder</span>
                </template>
              </LibraryFolderContentSection>

              <LibraryFolderContentSection
                :contentObjects="strategies"
                contentTypeTitle="Strategies"
                :numberOfItems="2"
                :saved="true"
                :removableContents="false"
                :shared="true"
                data-cy="strategies-section"
                :clickable="!isTrial"
              >
                <template #noContentMessage>
                  <span class="text-gray-700">No strategies in this folder</span>
                </template>
              </LibraryFolderContentSection>

              <LibraryFolderContentSection
                contentTypeTitle="Resources"
                :contentObjects="resources"
                :gridView="false"
                :removableContents="false"
                data-cy="resources-section"
                :shared="true"
              >
                <template #noContentMessage>
                  <span class="text-gray-700">No resources in this folder</span>
                </template>
              </LibraryFolderContentSection>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
  <PremiumActionModal
    v-model:external-open="openPremiumAction">
    <template #bodyContent>
      To copy a folder, you must have an active membership through your school or district. Contact your administrator to purchase or renew your premium membership.
    </template>
  </PremiumActionModal>

  <CopyFolderModal
    v-model="openCopyModal"
  />
</template>
